import type {
  ClinicalGeometryType,
  ClinicalLaterality,
} from "../../types";

export interface AnatomicalPoint {
  x: number;
  y: number;
}

export type AnatomicalGeometry =
  | {
      x: number;
      y: number;
    }
  | {
      points: AnatomicalPoint[];
    };

export interface GeometryValidationResult {
  valid: boolean;
  message?: string;
}

export function clampCoordinate(
  value: number
): number {
  return Number(
    Math.max(
      0,
      Math.min(1, value)
    ).toFixed(4)
  );
}

export function normalizePoint(
  point: AnatomicalPoint
): AnatomicalPoint {
  return {
    x: clampCoordinate(point.x),
    y: clampCoordinate(point.y),
  };
}

export function isAnatomicalPoint(
  value: unknown
): value is AnatomicalPoint {
  if (
    typeof value !== "object" ||
    value === null
  ) {
    return false;
  }

  const point =
    value as {
      x?: unknown;
      y?: unknown;
    };

  return (
    typeof point.x === "number" &&
    Number.isFinite(point.x) &&
    point.x >= 0 &&
    point.x <= 1 &&
    typeof point.y === "number" &&
    Number.isFinite(point.y) &&
    point.y >= 0 &&
    point.y <= 1
  );
}

export function geometryPoints(
  geometry: Record<string, unknown>
): AnatomicalPoint[] {
  if (!Array.isArray(geometry.points)) {
    return [];
  }

  return geometry.points.filter(
    isAnatomicalPoint
  );
}

export function validateGeometry(
  geometryType: ClinicalGeometryType,
  geometry: Record<string, unknown>
): GeometryValidationResult {
  if (geometryType === "POINT") {
    if (!isAnatomicalPoint(geometry)) {
      return {
        valid: false,
        message:
          "El punto debe estar ubicado dentro del mapa.",
      };
    }

    return { valid: true };
  }

  const points =
    geometryPoints(geometry);

  if (
    geometryType === "LINE" &&
    points.length < 2
  ) {
    return {
      valid: false,
      message:
        "La línea requiere al menos dos puntos.",
    };
  }

  if (
    geometryType === "AREA" &&
    points.length < 3
  ) {
    return {
      valid: false,
      message:
        "El área requiere al menos tres puntos.",
    };
  }

  if (points.length > 200) {
    return {
      valid: false,
      message:
        "Una geometría no puede superar 200 puntos.",
    };
  }

  return { valid: true };
}

export function buildGeometry(
  geometryType: ClinicalGeometryType,
  points: AnatomicalPoint[]
): Record<string, unknown> {
  const normalized =
    points.map(normalizePoint);

  if (
    geometryType === "POINT"
  ) {
    const point = normalized[0];

    return point
      ? {
          x: point.x,
          y: point.y,
        }
      : {};
  }

  return {
    points: normalized,
  };
}

export function inferLaterality(
  geometry: Record<string, unknown>,
  explicit?: ClinicalLaterality | null
): ClinicalLaterality {
  if (explicit) {
    return explicit;
  }

  let xValues: number[] = [];

  if (isAnatomicalPoint(geometry)) {
    xValues = [geometry.x];
  } else {
    xValues =
      geometryPoints(geometry).map(
        (point) => point.x
      );
  }

  if (xValues.length === 0) {
    return "NA";
  }

  const average =
    xValues.reduce(
      (sum, value) => sum + value,
      0
    ) / xValues.length;

  if (average < 0.46) {
    return "RIGHT";
  }

  if (average > 0.54) {
    return "LEFT";
  }

  return "MIDLINE";
}

export function markerColor(
  markerType: string
): string {
  switch (markerType) {
    case "PROBLEM":
      return "#d08a32";
    case "DIAGNOSIS":
      return "#9a4052";
    case "TREATMENT":
      return "#b86d55";
    case "OBSERVATION":
      return "#527d78";
    default:
      return "#667085";
  }
}

export function geometrySummary(
  geometryType: ClinicalGeometryType,
  geometry: Record<string, unknown>
): string {
  if (
    geometryType === "POINT" &&
    isAnatomicalPoint(geometry)
  ) {
    return `Punto ${geometry.x.toFixed(3)}, ${geometry.y.toFixed(3)}`;
  }

  const count =
    geometryPoints(geometry).length;

  if (geometryType === "LINE") {
    return `Línea de ${count} puntos`;
  }

  return `Área de ${count} vértices`;
}
